import { useEffect, useRef, useState, type RefObject } from "react";
import type { MediaStreamLike } from "@cuekards/ui";

/**
 * A `<video>` showing the host's own camera, wherever one is drawn (web-ui ticket
 * 09).
 *
 * There are three of them now — the green room's stage, the prompter's backdrop,
 * and the settings screen's device thumbnail — and until this ticket the effect
 * below was written out once per screen, identically, comments and all. Three
 * copies of a rule about a DOM property is how one of them ends up keeping a
 * stopped track alive after its component has gone.
 *
 * **It only ever shows a stream it was handed.** Nothing here opens a device:
 * `useCapture` is mounted once in `WebApp`, above all three screens, and is the
 * only thing in either host that calls `getUserMedia` — which is what makes
 * FR-31's "the camera is opened exactly once" a property of the code rather than
 * a rule to remember. A hook that could open one would be the obvious place for
 * the fourth screen to get its picture from, and the symptom would not be an
 * error: it would be the camera closing and reopening at the end of a countdown.
 *
 * What is deliberately *not* here is the mirror. The self-view is flipped so it
 * behaves like a mirror rather than a monitor, and the recorded file is not — but
 * that is a CSS transform on each element, and the three elements are three
 * different sizes in three different places. The stream handed to
 * `MediaRecorder` is this same object with nothing between it and the recorder,
 * which is what keeps the distinction free.
 */
export function useSelfView(
  stream: MediaStreamLike | null,
): RefObject<HTMLVideoElement> {
  const video = useRef<HTMLVideoElement>(null);

  /**
   * The identity of the *track*, not of the stream that carries it.
   *
   * The engine keeps one `MediaStream` object across a device toggle on purpose:
   * switching the camera off stops the video track and removes it, and switching
   * it back on grafts a freshly opened track into the stream already in hand
   * rather than opening a second stream. That is what keeps the microphone out of
   * a decision about the camera — and it means the stream a bound element is
   * holding is never replaced, so an effect keyed on the stream alone never runs
   * again. The element then paints frames from a track it was never told about,
   * inside the intrinsic size of the track that left: a stretched picture that
   * only a reload recovers from.
   */
  const trackId =
    stream === null
      ? null
      : // The cast is ticket 04's stated preference over widening
        // `MediaStreamLike`, which is a port and deliberately narrow.
        ((stream as MediaStream).getVideoTracks()[0]?.id ?? null);

  useEffect(() => {
    const element = video.current;
    if (element === null) return;
    if (stream === null) {
      // `srcObject` rather than a blob URL, and the cast is ticket 04's stated
      // preference over widening `MediaStreamLike` to satisfy the DOM.
      element.srcObject = null;
      return;
    }
    // Through null and back, because assigning the same object twice is a no-op
    // and this is very often the same object — see `trackId` above. The gap is a
    // frame of a picture that was switched off anyway.
    element.srcObject = null;
    element.srcObject = stream as MediaStream;
    // Autoplay of a muted, camera-sourced element is allowed, and a browser is
    // still entitled to refuse. A refusal leaves a still first frame, which is
    // worth nothing to argue about on any of the three screens this draws.
    void element.play().catch(() => {});
    return () => {
      element.srcObject = null;
    };
  }, [stream, trackId]);

  return video;
}

/**
 * The shape of the picture, as the element itself reports it (`videoWidth` over
 * `videoHeight`), or null until there has been one.
 *
 * It exists so a self-view can be *the size of what is being recorded*. Nothing
 * on the recording path crops, scales or reframes anything — the recorder is
 * handed the raw stream — so every difference between what you see and what you
 * get is a preview element being `object-fit: cover`ed into a box whose shape
 * came from the page layout rather than from the camera. A 4:3 camera in a 2:1
 * well shows the middle two thirds of the frame and records all of it, and the
 * only way to notice is to open the file.
 *
 * Read from the element rather than from `track.getSettings()`: the element is
 * what is actually on screen, `resize` is the event for "these dimensions just
 * changed", and it covers a camera renegotiating mid-session as well as one being
 * swapped for another of a different shape.
 *
 * It never goes back to null once it has a reading. A stream being detached
 * empties the element, and forgetting the shape there would collapse the well for
 * the moment a camera is off and jump it back on the moment it returns. Whether
 * there is a picture *right now* is a thing the caller already knows.
 */
export function useFrameAspect(video: RefObject<HTMLVideoElement>): number | null {
  const [aspect, setAspect] = useState<number | null>(null);

  useEffect(() => {
    const element = video.current;
    if (element === null) return;
    const read = (): void => {
      const width = element.videoWidth;
      const height = element.videoHeight;
      if (width > 0 && height > 0) setAspect(width / height);
    };
    // Once now: the metadata may already have arrived, in which case neither
    // event is coming.
    read();
    element.addEventListener("resize", read);
    element.addEventListener("loadedmetadata", read);
    return () => {
      element.removeEventListener("resize", read);
      element.removeEventListener("loadedmetadata", read);
    };
  }, [video]);

  return aspect;
}
